import { inject, Injectable, signal } from '@angular/core';
import { Observable, catchError, from, tap, throwError } from 'rxjs';
import {
  Area,
  AreaList,
  AreasApi,
  CreateAreaRequest,
  UpdateAreaRequest,
} from '@tourfold/libraries/tf-api-client-public-v2';

/**
 * Singleton service for the Areas list. Holds a memory cache of the last list response so
 * that re-navigating to the Areas page renders immediately from cached data and refreshes
 * in the background, instead of always falling back to the skeleton ("stale-while-revalidate"
 * pattern). The cache lives on the service (providedIn: 'root'), not on the component,
 * because the component is destroyed and re-created on every route change.
 */
@Injectable({ providedIn: 'root' })
export class AreasService {
  private readonly areasApi = inject(AreasApi);

  private readonly _cachedAreas = signal<readonly Area[] | null>(null);
  /**
   * Last successful list response. `null` means we've never loaded yet (so the caller
   * should render its initial-load placeholder). After the first successful load this
   * signal always holds the most recent list, even across component re-mounts.
   */
  readonly cachedAreas = this._cachedAreas.asReadonly();

  listAreas(page = 0, size = 200): Observable<AreaList> {
    return from(this.areasApi.listAreas({ page, size })).pipe(
      tap((response) => this._cachedAreas.set(response.items ?? [])),
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  createArea(createAreaRequest: CreateAreaRequest): Observable<Area> {
    return from(this.areasApi.createArea({ createAreaRequest })).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  updateArea(id: string, updateAreaRequest: UpdateAreaRequest): Observable<Area> {
    return from(this.areasApi.updateArea({ id, updateAreaRequest })).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  deleteArea(id: string): Observable<void> {
    return from(this.areasApi.deleteArea({ id })).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }
}
